import { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const SESSION_CHECK_INTERVAL = 30000;
const IDLE_TIMEOUT = 15 * 60 * 1000;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [sessionValid, setSessionValid] = useState(true);
  const lastActivity = useRef(Date.now());
  const sessionCheckTimer = useRef(null);
  const idleCheckTimer = useRef(null);

  const handleLogout = useCallback(async () => {
    try {
      await api.post('/api/super-admin-logout');
    } catch (_) {}
    localStorage.removeItem('superAdminToken');
    setSessionValid(false);
  }, []);

  const resetIdleTimer = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer));

    sessionCheckTimer.current = setInterval(async () => {
      try {
        const res = await api.get('/api/super-admin-session-check');
        if (!res.data.loggedIn) {
          handleLogout();
        }
      } catch (err) {
        if (err.response?.status === 401) {
          handleLogout();
        }
      }
    }, SESSION_CHECK_INTERVAL);

    idleCheckTimer.current = setInterval(() => {
      if (Date.now() - lastActivity.current > IDLE_TIMEOUT) {
        handleLogout();
      }
    }, 10000);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      clearInterval(sessionCheckTimer.current);
      clearInterval(idleCheckTimer.current);
    };
  }, [isAuthenticated, resetIdleTimer, handleLogout]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || !sessionValid) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
